import { Injectable } from "@angular/core";
import { links } from "src/app/utils/constants";

import { EventsService } from "./events.service";
import { ALERT_SERVICE, AlertConfirm, AlertToast } from "../models";

/** Simple AlertController & ToastController Implementation */
@Injectable({
  providedIn: "root",
})
export class AlertService {
  /** @ignore */
  constructor(private events: EventsService) {}

  /**
   *  Opens Basic OK AlertController
   * @param title Alert box heading
   * @param subHeader Alert box sub-heading
   * @param message Content of alert box
   * @param handler for OK button logic
   */
  async showAlert({
    title,
    subHeader,
    message,
    handler,
  }: _AlertProps): Promise<void> {
    this.events.publish<AlertConfirm>(ALERT_SERVICE.confirm, {
      header: title,
      message: subHeader ? subHeader + " - " + message : message,
      rejectVisible: false,
      acceptButtonProps: {
        label: "OK",
      },
      accept: handler,
    });
  }

  /**
   * @depreacted use .confirm() instead
   *
   * Opens AlertController
   * @param title Confirm box heading
   * @param subHeader Confirm box sub-heading
   * @param message Content of confirm box
   * @param btnText Custom button, other button is cancel
   * @param handler for custom button
   * @param cancelHandler for cancel button
   */
  async showConfirm(
    title: string,
    subHeader: string,
    message: string,
    btnText: string,
    handler: any,
    cancelHandler?: any
  ): Promise<void> {
    const isDestructive = cancelHandler !== undefined;

    this.events.publish<AlertConfirm>(ALERT_SERVICE.confirm, {
      header: title,
      message: subHeader ? subHeader + " - " + message : message,
      rejectButtonProps: {
        label: "Cancel",
        outlined: true,
      },
      acceptButtonProps: {
        label: btnText,
        severity: isDestructive ? "danger" : "secondary",
      },
      accept: handler,
      reject: cancelHandler,
      destructive: isDestructive,
    });
  }

  /**
   *  Opens AlertController
   * @param title Confirm box heading
   * @param message Content of confirm box
   * @param btnText Confirmation/Accept text
   * @param handler Confirmation/Accept handler
   * @param cancelHandler for cancel button
   */
  async confirm({
    title,
    message,
    buttonText,
    handler,
    cancelHandler,
  }: _AlertConfirm): Promise<void> {
    const isDestructive = cancelHandler !== undefined;

    this.events.publish<AlertConfirm>(ALERT_SERVICE.confirm, {
      header: title,
      message: message,
      rejectButtonProps: {
        label: "Cancel",
        outlined: true,
      },
      acceptButtonProps: {
        label: buttonText,
        severity: isDestructive ? "danger" : "secondary",
      },
      accept: handler,
      reject: cancelHandler,
      destructive: isDestructive,
    });
  }

  /**
   *  Opens AlertController
   * @param title Confirm box heading
   * @param subHeader Confirm box sub-heading
   * @param message Content of confirm box
   * @param btnText Custom button, other button is cancel
   * @param handler for custom button
   * @param cancelHandler for cancel button
   */
  async showGetPremium({
    title = "Get DefectWise Premium",
    message,
    utmContentLink,
    btnText = "Learn More",
  }: _GetPremiumProps): Promise<void> {
    this.events.publish<AlertConfirm>(ALERT_SERVICE.upgrade, {
      header: title,
      message: message,
      acceptButtonProps: {
        label: btnText,
      },
      rejectButtonProps: {
        label: "No, thanks",
        outlined: true,
      },
      accept: () => {
        window.open(
          `${links.pricing}?utm_source=web_app_link&utm_medium=web_app&utm_campaign=web_app&utm_content=${utmContentLink}`,
          "_blank"
        );
      },
    });
  }

  /**
   *  Opens Message
   * @param message Toast message
   * @param duration Time to show in milliseconds
   * @param color Color of toast when displayed
   */
  async showToast(
    message: string,
    severity?: "error" | "success" | "warn" | "info" | undefined,
    duration?: number
  ): Promise<void> {
    const _duration = duration || 2000;

    this.events.publish<AlertToast>(ALERT_SERVICE.toast, {
      severity: severity ?? "success",
      summary: severity === "error" ? "Error" : "Success",
      detail: message,
      life: _duration,
    });
  }
}

interface _AlertProps {
  title: string;
  subHeader?: string;
  message: string;
  handler?: any;
}

interface _GetPremiumProps {
  title?: string;
  message: string;
  utmContentLink: string;
  btnText?: string;
}

interface _AlertConfirm {
  title: string;
  message: string;
  buttonText: string;
  handler: any;
  cancelHandler?: any;
}
